// src/pages/supervisor/SupervisorResearchWorkView.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Chip,
    Grid,
    Slide,
    IconButton,
    Tabs,
    Tab,
    Avatar,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    LinearProgress,
    Badge,
    useMediaQuery,
    TextField,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Divider,
    Rating,
    MenuItem
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Description,
    HealthAndSafety,
    MenuBook,
    Article,
    Storage,
    Slideshow,
    Photo,
    Attachment,
    InsertDriveFile,
    Download,
    Warning,
    Search,
    FilterList as FilterIcon,
    ClearAll,
    DateRange,
    School,
    Visibility,
    Close,
    ExpandMore,
    CheckCircle,
    Cancel,
    Schedule,
    Pending,
    RateReview,
    Feedback,
    Assignment as AssignmentIcon,
    Category,
    SupervisorAccount,
    CloudUpload,
    AccountBalance
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIResearchWorkReviewStore,
    APIResearchWorkUploadRevision,
    APIResearchWorkListBySupervisor
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails 
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import SupervisorBar from '../../components/SupervisorBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Helper function to get initials
const getInitials = (name) => {
    if (!name) return 'N/A';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Helper function to get supervisor name
const getSupervisorName = (work) => {
    if (!work) return '';
    return work.supervisor_name || work.latest_review?.supervisor_name || '';
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const SupervisorResearchWorkView = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [works, setWorks] = useState([]);
    const [filteredWorks, setFilteredWorks] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedWork, setSelectedWork] = useState(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openReviewDialog, setOpenReviewDialog] = useState(false);
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [revisionFile, setRevisionFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    
    const [reviewForm, setReviewForm] = useState({
        rating: 0,
        comments: '',
        feedback: '',
        status: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isMounted = useRef(true);
    const supervisorId = getUserDetails("Id");

    // Filters
    const [filters, setFilters] = useState({
        work_type: '',
        work_status: '',
        specialty_id: '',
        university_id: '',
        dateFrom: '',
        dateTo: ''
    });
    const [filterOptions, setFilterOptions] = useState(false);

    // Work type options for filtering
    const workTypes = [
        { value: 'proposal', label: 'Research Proposal', icon: <Description />, color: 'primary' },
        { value: 'ethics_application', label: 'Ethics Application', icon: <HealthAndSafety />, color: 'secondary' },
        { value: 'chapter', label: 'Thesis Chapter', icon: <MenuBook />, color: 'info' },
        { value: 'full_thesis', label: 'Full Thesis', icon: <Article />, color: 'success' },
        { value: 'dataset', label: 'Dataset', icon: <Storage />, color: 'warning' },
        { value: 'presentation', label: 'Presentation', icon: <Slideshow />, color: 'error' },
        { value: 'poster', label: 'Poster', icon: <Photo />, color: 'default' },
        { value: 'supplementary_material', label: 'Supplementary Material', icon: <Attachment />, color: 'info' }
    ];

    // Work status options
    const workStatusOptions = [
        { value: 'submitted', label: 'Pending Review', color: 'warning', icon: <Schedule />, action: 'review' },
        { value: 'under_review', label: 'Under Review', color: 'info', icon: <Pending />, action: 'review' },
        { value: 'revision_requested', label: 'Revision Needed', color: 'warning', icon: <Pending />, action: 'review' },
        { value: 'approved', label: 'Approved', color: 'success', icon: <CheckCircle />, action: 'view' },
        { value: 'rejected', label: 'Rejected', color: 'error', icon: <Cancel />, action: 'view' }
    ];

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchWorks();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        applyFilters();
    }, [works, searchTerm, filters, activeTab]);

    const fetchWorks = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const supId = getUserDetails("Id");
            const result = await apiService.listById(APIResearchWorkListBySupervisor, supId);
            if (result.success && isMounted.current) {
                setWorks(Array.isArray(result.data) ? result.data : []);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load research works");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    // Get unique specialties from works for filter dropdown
    const getUniqueSpecialties = () => {
        const specialties = new Map();
        works.forEach(work => {
            if (work.specialty_id && work.specialty_name) {
                specialties.set(work.specialty_id, {
                    id: work.specialty_id,
                    name: work.specialty_name,
                    code: work.specialty_code
                });
            }
        });
        return Array.from(specialties.values());
    };

    // Get unique universities from works for filter dropdown
    const getUniqueUniversities = () => {
        const universities = new Map();
        works.forEach(work => {
            if (work.university_id && work.university_name) {
                universities.set(work.university_id, {
                    id: work.university_id,
                    name: work.university_name
                });
            }
        });
        return Array.from(universities.values());
    };

    const applyFilters = () => {
        let filtered = [...works];
        
        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(work =>
                work.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                work.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                work.upload_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                work.specialty_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                work.university_name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // Work type filter
        if (filters.work_type) {
            filtered = filtered.filter(work => work.work_type === filters.work_type);
        }
        
        // Work status filter
        if (filters.work_status) {
            filtered = filtered.filter(work => work.work_status === filters.work_status);
        }
        
        // Specialty filter
        if (filters.specialty_id) {
            filtered = filtered.filter(work => work.specialty_id === parseInt(filters.specialty_id));
        }
        
        // University filter
        if (filters.university_id) {
            filtered = filtered.filter(work => work.university_id === parseInt(filters.university_id));
        }
        
        // Date filter
        if (filters.dateFrom) {
            filtered = filtered.filter(work => 
                work.submitted_at && work.submitted_at.split('T')[0] >= filters.dateFrom
            );
        }
        if (filters.dateTo) {
            filtered = filtered.filter(work => 
                work.submitted_at && work.submitted_at.split('T')[0] <= filters.dateTo
            );
        }
        
        // Tab filter
        if (activeTab === 1) {
            filtered = filtered.filter(work => work.work_status === 'submitted' || work.work_status === 'under_review');
        } else if (activeTab === 2) {
            filtered = filtered.filter(work => work.work_status === 'revision_requested');
        } else if (activeTab === 3) {
            filtered = filtered.filter(work => work.work_status === 'approved');
        }
        
        setFilteredWorks(filtered);
        setPage(0);
    };

    const handleOpenViewDialog = async (work) => {
        setSelectedWork(work);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedWork(null);
    };

    const handleOpenReviewDialog = (work) => {
        setSelectedWork(work);
        setReviewForm({
            rating: work.latest_review?.score || 0,
            comments: work.latest_review?.review_comments || '',
            feedback: work.revision_feedback || '',
            status: ''
        });
        setOpenReviewDialog(true);
    };

    const handleCloseReviewDialog = () => {
        setOpenReviewDialog(false);
        setSelectedWork(null);
        setReviewForm({
            rating: 0,
            comments: '',
            feedback: '',
            status: ''
        });
    };

    // Revision Upload Dialog Actions
    const handleOpenUploadDialog = (work) => {
        setSelectedWork(work);
        setOpenUploadDialog(true);
    };

    const handleCloseUploadDialog = () => {
        setOpenUploadDialog(false);
        setSelectedWork(null);
        setRevisionFile(null);
        setIsUploading(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const fileExtension = file.name.split('.').pop().toLowerCase();
            if (fileExtension !== 'doc' && fileExtension !== 'docx') {
                showToast("Error", "Please select a Word document (.doc or .docx)", "error");
                return;
            }
            setRevisionFile(file);
        }
    };

    const handleSubmitRevisionFile = async () => {
        if (!revisionFile) {
            showToast("Error", "Please select a file to upload", "error");
            return;
        }

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('topic_id', selectedWork.id);
            formData.append('file', revisionFile);

            const endpoint = APIResearchWorkUploadRevision || '/api/research-topic/upload-revision';
            const result = await apiService.create(endpoint, formData);

            if (result && result.success) {
                showToast("Success", "Revised document uploaded successfully", "success");
                handleCloseUploadDialog();
                fetchWorks();
            } else {
                showToast("Error", result.message || "Failed to upload revision file", "error");
            }
        } catch (error) {
            console.error("Revision upload error:", error);
            showToast("Error", error.response?.data?.message || "Failed to upload revision file", "error");
        } finally {
            setIsUploading(false);
        }
    };

    const handleReviewChange = (e) => {
        const { name, value } = e.target;
        setReviewForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitReview = async (status) => {
        if (!reviewForm.comments && status !== 'approved') {
            showToast("Error", "Please provide review comments", "error");
            return;
        }

        if (status === 'revision_requested' && !reviewForm.feedback) {
            showToast("Error", "Please provide revision feedback", "error");
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('upload_id', selectedWork.id);
            formData.append('supervisor_id', supervisorId);
            formData.append('status', status);
            formData.append('review_comments', reviewForm.comments);
            if (reviewForm.rating > 0) {
                formData.append('score', reviewForm.rating);
            }
            if (reviewForm.feedback && status === 'revision_requested') {
                formData.append('revision_feedback', reviewForm.feedback);
            }
            formData.append('review_type', 'initial');

            const result = await apiService.create(APIResearchWorkReviewStore, formData);

            if (result && result.success) {
                showToast("Success", `Work ${status === 'approved' ? 'approved' : status === 'revision_requested' ? 'sent for revision' : 'reviewed'} successfully`, "success");
                handleCloseReviewDialog();
                fetchWorks();
            } else {
                showToast("Error", result.message || "Failed to submit review", "error");
            }
        } catch (error) {
            console.error("Review submission error:", error);
            showToast("Error", error.response?.data?.message || "Failed to submit review", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDownload = (work) => {
        if (work.file_url) {
            window.open(work.file_url, '_blank');
        }
    };

    const getWorkTypeIcon = (type) => {
        const found = workTypes.find(t => t.value === type);
        return found?.icon || <InsertDriveFile />;
    };

    const getWorkTypeColor = (type) => {
        const found = workTypes.find(t => t.value === type);
        return found?.color || 'default';
    };

    const getWorkTypeLabel = (type) => {
        const found = workTypes.find(t => t.value === type);
        return found?.label || type;
    };

    const getWorkStatusColor = (status) => {
        const found = workStatusOptions.find(s => s.value === status);
        return found?.color || 'default';
    };

    const getWorkStatusLabel = (status) => {
        const found = workStatusOptions.find(s => s.value === status);
        return found?.label || status;
    };

    const canReview = (status) => {
        return status === 'submitted' || status === 'under_review' || status === 'revision_requested';
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters(prev => ({ ...prev, dateFrom: today, dateTo: today }));
        setFilterOptions(false);
    };

    const clearFilters = () => {
        setFilters({
            work_type: '',
            work_status: '',
            specialty_id: '',
            university_id: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        setActiveTab(0);
        setFilterOptions(false);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return <LoadingState message="Loading Research Works..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchWorks} title="Unable to Load Research Works" variant="research" fullPage retryText="Retry Loading" />;
    }

    const statusTabs = [
        { value: 0, label: 'All Submissions', count: works.length, icon: <Description /> },
        { value: 1, label: 'Pending Review', count: works.filter(w => w.work_status === 'submitted' || w.work_status === 'under_review').length, icon: <Schedule /> },
        { value: 2, label: 'Revision Needed', count: works.filter(w => w.work_status === 'revision_requested').length, icon: <Pending /> },
        { value: 3, label: 'Approved', count: works.filter(w => w.work_status === 'approved').length, icon: <CheckCircle /> }
    ];

    return (
        <FullWidthContainer >
            <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                <SupervisorBar notificationCount={5} />
                
                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 'bold',
                            color: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            <AssignmentIcon /> Student Research Works
                        </Typography>

                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            width: { xs: '100%', sm: 'auto' },
                            justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={() => setFilterOptions(true)}
                                sx={{ 
                                    textTransform: 'none', 
                                    borderRadius: '8px',
                                    flex: { xs: 1, sm: 'none' }
                                }}
                            >
                                View Options
                            </Button>
                            <Button
                                variant="outlined"
                                color="info"
                                startIcon={<RefreshIcon />}
                                onClick={fetchWorks}
                                sx={{ 
                                    textTransform: 'none', 
                                    borderRadius: '8px',
                                    flex: { xs: 1, sm: 'none' }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>

                    {/* Search Bar */}
                    <Paper sx={{
                        mt: 2,
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '8px',
                        backgroundColor: theme.palette.grey[50],
                    }}>
                        <Search sx={{ mx: 1, color: 'text.secondary' }} />
                        <TextField 
                            fullWidth 
                            placeholder="Search by title, student name, upload ID, specialty, or university..." 
                            variant="standard" 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            InputProps={{ disableUnderline: true }}
                            size="small"
                        />
                    </Paper>

                    {/* Active Filters */}
                    {(filters.work_type || filters.work_status || filters.specialty_id || filters.university_id || filters.dateFrom || filters.dateTo) && (
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            mt: 2,
                            pt: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                Active filters:
                            </Typography>
                            {filters.work_type && (
                                <Chip
                                    label={`Type: ${getWorkTypeLabel(filters.work_type)}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, work_type: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {filters.work_status && (
                                <Chip
                                    label={`Status: ${getWorkStatusLabel(filters.work_status)}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, work_status: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {filters.specialty_id && (
                                <Chip
                                    label={`Specialty: ${getUniqueSpecialties().find(s => s.id === parseInt(filters.specialty_id))?.name || filters.specialty_id}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, specialty_id: '' }))}
                                    color="primary"
                                    variant="outlined"
                                    icon={<Category />}
                                />
                            )}
                            {filters.university_id && (
                                <Chip
                                    label={`University: ${getUniqueUniversities().find(u => u.id === parseInt(filters.university_id))?.name || filters.university_id}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, university_id: '' }))}
                                    color="primary"
                                    variant="outlined"
                                    icon={<AccountBalance />}
                                />
                            )}
                            {filters.dateFrom && (
                                <Chip
                                    label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {filters.dateTo && (
                                <Chip
                                    label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            <Button size="small" onClick={clearFilters} startIcon={<ClearAll />}>
                                Clear All
                            </Button>
                        </Box>
                    )}
                </Paper>

                {/* Status Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mb: { xs: 2, sm: 3 }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        {statusTabs.map(tab => (
                            <Tab 
                                key={tab.value}
                                icon={tab.icon}
                                label={
                                    <Badge badgeContent={tab.count} color="primary" sx={{ mr: 1 }}>
                                        {tab.label}
                                    </Badge>
                                } 
                                value={tab.value}
                            />
                        ))}
                    </Tabs>
                </Paper>
            </Box>

            {/* Works Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'auto',
                    backgroundColor: 'white',
                }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: { xs: 900, sm: 1000, md: 1200 } }}>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Student</TableCell>
                                    <TableCell>Title</TableCell>
                                    <TableCell>University</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Supervisor</TableCell>
                                    <TableCell>Submitted</TableCell>
                                    <TableCell>Deadline</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredWorks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((work, index) => (
                                    <TableRow
                                        key={work.id}
                                        hover
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.action.hover
                                            },
                                            ...(work.is_overdue && {
                                                backgroundColor: theme.palette.error.light + '10',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.error.light + '20',
                                                }
                                            })
                                        }}
                                    >
                                        <TableCell>
                                            <Chip label={page * rowsPerPage + index + 1} color="primary" size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                                                    {getInitials(work.student_name)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {work.student_name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Article fontSize="small" color="primary" />
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {work.title?.substring(0, 50)}...
                                                </Typography>
                                            </Box>
                                            {work.file_url && (
                                                <Tooltip title="Download original Word document">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleDownload(work)}
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        <Description fontSize="small" color="primary" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                
                                        <TableCell>
                                            {work.university_name ? (
                                                <Chip
                                                    icon={<AccountBalance />}
                                                    label={work.university_name}
                                                    size="small"
                                                    color="secondary"
                                                    variant="outlined"
                                                />
                                            ) : (
                                                <Chip
                                                    label="No University"
                                                    size="small"
                                                    variant="outlined"
                                                    color="default"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getWorkTypeIcon(work.work_type)}
                                                label={getWorkTypeLabel(work.work_type)}
                                                size="small"
                                                color={getWorkTypeColor(work.work_type)}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={work.work_status_icon}
                                                label={work.formatted_work_status}
                                                size="small"
                                                color={work.work_status_color}
                                            />
                                            {work.is_overdue && work.work_status !== 'approved' && (
                                                <Chip label="Overdue" color="error" size="small" sx={{ mt: 0.5 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <SupervisorAccount fontSize="small" color="primary" />
                                                <Typography variant="body2">
                                                    {getSupervisorName(work) || 'Not Assigned'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{formatCreatedAtDate(work.submitted_at)}</TableCell>
                                        <TableCell>
                                            {work.review_deadline ? (
                                                <Box>
                                                    <Typography variant="caption">
                                                        {formatCreatedAtDate(work.review_deadline)}
                                                    </Typography>
                                                    {work.is_overdue && (
                                                        <Chip label="OVERDUE" color="error" size="small" sx={{ ml: 1, height: 20 }} />
                                                    )}
                                                </Box>
                                            ) : 'Not set'}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                <Tooltip title="View Details">
                                                    <IconButton onClick={() => handleOpenViewDialog(work)} color="primary" size="small">
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                                {canReview(work.work_status) && (
                                                    <Tooltip title="Review Work">
                                                        <IconButton onClick={() => handleOpenReviewDialog(work)} color="secondary" size="small">
                                                            <RateReview />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {/* Upload Revision Action */}
                                                <Tooltip title="Upload Supervisor Revision File">
                                                    <IconButton onClick={() => handleOpenUploadDialog(work)} color="warning" size="small">
                                                        <CloudUpload />
                                                    </IconButton>
                                                </Tooltip>
                                                
                                                {/* Download Original File Action */}
                                                <Tooltip title="Download Original Submission">
                                                    <IconButton onClick={() => handleDownload(work)} color="info" size="small">
                                                        <Download />
                                                    </IconButton>
                                                </Tooltip>

                                                {/* Download Supervisor Revision Action (Conditional) */}
                                                {work.revision_file_url && (
                                                    <Tooltip title="Download Supervisor Revision">
                                                        <IconButton 
                                                            onClick={() => window.open(work.revision_file_url, '_blank')} 
                                                            color="success" 
                                                            size="small"
                                                        >
                                                            <Download />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredWorks.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredWorks.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <AssignmentIcon sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No research works found</Typography>
                            <Typography variant="body2">No students have submitted research works for review yet</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Filter Options Dialog - Now doesn't close on backdrop click */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={(event, reason) => {
                    // Only close if clicking the close button, not on backdrop click
                    if (reason === 'backdropClick') {
                        return;
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Works
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<DateRange />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Submissions
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Work Type
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Work Type</InputLabel>
                            <Select
                                name="work_type"
                                value={filters.work_type}
                                onChange={handleFilterChange}
                                label="Work Type"
                            >
                                <MenuItem value="">All Types</MenuItem>
                                {workTypes.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {type.icon}
                                            {type.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Status
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Work Status</InputLabel>
                            <Select
                                name="work_status"
                                value={filters.work_status}
                                onChange={handleFilterChange}
                                label="Work Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {workStatusOptions.map((status) => (
                                    <MenuItem key={status.value} value={status.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {status.icon}
                                            {status.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Specialty
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Research Specialty</InputLabel>
                            <Select
                                name="specialty_id"
                                value={filters.specialty_id}
                                onChange={handleFilterChange}
                                label="Research Specialty"
                            >
                                <MenuItem value="">All Specialties</MenuItem>
                                {getUniqueSpecialties().map((specialty) => (
                                    <MenuItem key={specialty.id} value={specialty.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Category />
                                            {specialty.name}
                                            {specialty.code && ` (${specialty.code})`}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by University
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>University</InputLabel>
                            <Select
                                name="university_id"
                                value={filters.university_id}
                                onChange={handleFilterChange}
                                label="University"
                            >
                                <MenuItem value="">All Universities</MenuItem>
                                {getUniqueUniversities().map((university) => (
                                    <MenuItem key={university.id} value={university.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AccountBalance />
                                            {university.name}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center', gap: 2 }}>
                    <Button 
                        color="error" 
                        onClick={() => setFilterOptions(false)} 
                        variant="contained"
                        startIcon={<Close />}
                    >
                        Close
                    </Button>
                    <Button 
                        onClick={() => setFilterOptions(false)} 
                        color="primary" 
                        variant="contained"
                        startIcon={<FilterIcon />}
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Details Dialog */}
            <Dialog
                open={openViewDialog}
                onClose={handleCloseViewDialog}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogTitle sx={{ 
                    textAlign: 'center',
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Research Work Details</Typography>
                    <IconButton
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={handleCloseViewDialog}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedWork && (
                        <Box sx={{ p: 2 }}>
                            {/* Header Section */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold' }}>
                                    {selectedWork.upload_id}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {selectedWork.university_name && (
                                        <Chip
                                            icon={<AccountBalance />}
                                            label={selectedWork.university_name}
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    )}
                                    {selectedWork.specialty_name && (
                                        <Chip
                                            icon={<Category />}
                                            label={selectedWork.specialty_name}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                    <Chip
                                        icon={getWorkTypeIcon(selectedWork.work_type)}
                                        label={getWorkTypeLabel(selectedWork.work_type)}
                                        color={getWorkTypeColor(selectedWork.work_type)}
                                        variant="outlined"
                                    />
                                    <Chip
                                        icon={selectedWork.work_status_icon}
                                        label={selectedWork.formatted_work_status}
                                        color={selectedWork.work_status_color}
                                    />
                                </Box>
                            </Box>

                            {/* Work Information */}
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Work Information
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <strong>Title:</strong> {selectedWork.title}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <strong>Description:</strong> {selectedWork.description || 'Not provided'}
                                            </Typography>
                                        </Grid>
                                        {selectedWork.specialty_name && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Research Specialty:</strong>
                                                    <Chip 
                                                        icon={<Category />}
                                                        label={selectedWork.specialty_name}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ ml: 1 }}
                                                    />
                                                    {selectedWork.specialty_code && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                            (Code: {selectedWork.specialty_code})
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            </Grid>
                                        )}
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">
                                                <strong>Version:</strong> v{selectedWork.version_number}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">
                                                <strong>File Size:</strong> {selectedWork.file_size_mb} MB
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <strong>Word Count:</strong> {selectedWork.word_count?.toLocaleString() || 'Not specified'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* Student Information */}
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        <School sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Student Information
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <strong>Name:</strong> {selectedWork.student_name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <strong>Registration ID:</strong> {selectedWork.student_registration_id}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <strong>Email:</strong> {selectedWork.student_email}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* University Information */}
                            {selectedWork.university_name && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            <AccountBalance sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            University Information
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="subtitle2">
                                            <strong>University:</strong> {selectedWork.university_name}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Supervisor Information */}
                            {getSupervisorName(selectedWork) && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            <SupervisorAccount sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Supervisor Information
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="subtitle2">
                                            <strong>Name:</strong> {getSupervisorName(selectedWork)}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Plagiarism Information */}
                            {selectedWork.plagiarism_score && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Plagiarism Information
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Plagiarism Score:</strong> {selectedWork.plagiarism_score}%
                                                </Typography>
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={selectedWork.plagiarism_score}
                                                    color={selectedWork.plagiarism_score > 30 ? 'error' : selectedWork.plagiarism_score > 15 ? 'warning' : 'success'}
                                                    sx={{ height: 8, borderRadius: 4, mt: 1 }}
                                                />
                                            </Grid>
                                            {selectedWork.plagiarism_report_url && (
                                                <Grid item xs={12}>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<Download />}
                                                        href={selectedWork.plagiarism_report_url}
                                                        target="_blank"
                                                        size="small"
                                                    >
                                                        Download Plagiarism Report
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Previous Review Information */}
                            {selectedWork.latest_review && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            <RateReview sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Previous Review
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Reviewer:</strong> {selectedWork.latest_review.supervisor_name}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Review Comments:</strong>
                                                </Typography>
                                                <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], mt: 1 }}>
                                                    <Typography variant="body2">
                                                        {selectedWork.latest_review.review_comments}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            {selectedWork.latest_review.score && (
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Score:</strong> {selectedWork.latest_review.score}/100
                                                    </Typography>
                                                    <Rating 
                                                        value={selectedWork.latest_review.score / 20} 
                                                        readOnly 
                                                        precision={0.5}
                                                        sx={{ mt: 1 }}
                                                    />
                                                </Grid>
                                            )}
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Review Date:</strong> {formatCreatedAtDate(selectedWork.latest_review.review_submitted_at)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Revision Feedback */}
                            {selectedWork.revision_feedback && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                            <Feedback sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Revision Feedback
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedWork.revision_feedback}
                                        </Alert>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Dates */}
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        <DateRange sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Important Dates
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">
                                                <strong>Submitted:</strong> {formatCreatedAtDate(selectedWork.submitted_at)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">
                                                <strong>Last Modified:</strong> {formatCreatedAtDate(selectedWork.last_modified)}
                                            </Typography>
                                        </Grid>
                                        {selectedWork.review_deadline && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Review Deadline:</strong> {formatCreatedAtDate(selectedWork.review_deadline)}
                                                    {selectedWork.is_overdue && (
                                                        <Chip label="Overdue" color="error" size="small" sx={{ ml: 2 }} />
                                                    )}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* Download Section */}
                            {selectedWork.file_url && (
                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<Download />}
                                        onClick={() => handleDownload(selectedWork)}
                                        fullWidth
                                    >
                                        Download Original Work Document
                                    </Button>
                                </Box>
                            )}

                            {/* Revision Download Section */}
                            {selectedWork.revision_file_url && (
                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Download />}
                                        onClick={() => window.open(selectedWork.revision_file_url, '_blank')}
                                        fullWidth
                                    >
                                        Download Revised Document
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {canReview(selectedWork?.work_status) && (
                        <Button 
                            onClick={() => {
                                handleCloseViewDialog();
                                handleOpenReviewDialog(selectedWork);
                            }} 
                            variant="contained" 
                            color="secondary"
                            startIcon={<RateReview />}
                        >
                            Review This Work
                        </Button>
                    )}
                    <Button color="error" onClick={handleCloseViewDialog} variant="outlined">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Review Dialog */}
            <Dialog
                open={openReviewDialog}
                onClose={handleCloseReviewDialog}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ 
                    textAlign: 'center',
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <RateReview color="secondary" />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Review Research Work
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {selectedWork?.title}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {selectedWork && (
                        <Box sx={{ p: 2 }}>
                            {/* Student Info */}
                            <Alert severity="info" sx={{ mb: 3 }}>
                                <Typography variant="body2">
                                    <strong>Student:</strong> {selectedWork.student_name} ({selectedWork.student_registration_id})
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Work Type:</strong> {getWorkTypeLabel(selectedWork.work_type)}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Supervisor:</strong> {getSupervisorName(selectedWork) || 'Not Assigned'}
                                </Typography>
                                {selectedWork.specialty_name && (
                                    <Typography variant="body2">
                                        <strong>Specialty:</strong> {selectedWork.specialty_name}
                                    </Typography>
                                )}
                                {selectedWork.university_name && (
                                    <Typography variant="body2">
                                        <strong>University:</strong> {selectedWork.university_name}
                                    </Typography>
                                )}
                                {selectedWork.plagiarism_score && (
                                    <Typography variant="body2">
                                        <strong>Plagiarism Score:</strong> {selectedWork.plagiarism_score}%
                                    </Typography>
                                )}
                            </Alert>

                            {/* Rating */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Rating (Optional)
                            </Typography>
                            <Rating
                                name="rating"
                                value={reviewForm.rating / 20}
                                onChange={(event, newValue) => {
                                    setReviewForm(prev => ({ ...prev, rating: (newValue || 0) * 20 }));
                                }}
                                precision={0.5}
                                size="large"
                                sx={{ mb: 3 }}
                            />

                            {/* Review Comments */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Review Comments *
                            </Typography>
                            <TextField
                                fullWidth
                                name="comments"
                                multiline
                                rows={4}
                                value={reviewForm.comments}
                                onChange={handleReviewChange}
                                placeholder="Provide detailed feedback about the research work..."
                                required
                                sx={{ mb: 3 }}
                            />

                            {/* Revision Feedback (only for revision requests) */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.warning.main }}>
                                Revision Feedback
                            </Typography>
                            <TextField
                                fullWidth
                                name="feedback"
                                multiline
                                rows={3}
                                value={reviewForm.feedback}
                                onChange={handleReviewChange}
                                placeholder="If requesting revisions, specify what needs to be changed..."
                                helperText="Required only if you select 'Request Revisions'"
                                sx={{ mb: 3 }}
                            />

                            {/* Action Buttons */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                                Decision *
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="success"
                                        startIcon={<CheckCircle />}
                                        onClick={() => handleSubmitReview('approved')}
                                        disabled={isSubmitting}
                                        sx={{ py: 1.5 }}
                                    >
                                        Approve
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="warning"
                                        startIcon={<Pending />}
                                        onClick={() => handleSubmitReview('revision_requested')}
                                        disabled={isSubmitting}
                                        sx={{ py: 1.5 }}
                                    >
                                        Request Revisions
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="error"
                                        startIcon={<Cancel />}
                                        onClick={() => handleSubmitReview('rejected')}
                                        disabled={isSubmitting}
                                        sx={{ py: 1.5 }}
                                    >
                                        Reject
                                    </Button>
                                </Grid>
                            </Grid>
                            {isSubmitting && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseReviewDialog} variant="outlined" disabled={isSubmitting}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Upload Revision Dialog */}
            <Dialog
                open={openUploadDialog}
                onClose={handleCloseUploadDialog}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                    Upload Revised Document
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary" align="center">
                            Upload a revised Microsoft Word document (.doc or .docx) for "<strong>{selectedWork?.title}</strong>"
                        </Typography>
                        
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUpload />}
                            fullWidth
                            sx={{ py: 1.5, mt: 1 }}
                        >
                            Select Word Document
                            <input
                                type="file"
                                hidden
                                accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                onChange={handleFileChange}
                            />
                        </Button>
                        
                        {revisionFile && (
                            <Typography variant="caption" sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main', fontWeight: 'bold' }}>
                                <CheckCircle fontSize="inherit" /> Selected: {revisionFile.name}
                            </Typography>
                        )}
                        
                        {isUploading && (
                            <Box sx={{ width: '100%', mt: 2 }}>
                                <LinearProgress />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseUploadDialog} color="inherit" disabled={isUploading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSubmitRevisionFile} 
                        color="primary" 
                        variant="contained" 
                        disabled={!revisionFile || isUploading}
                    >
                        {isUploading ? 'Uploading...' : 'Submit Revision'}
                    </Button>
                </DialogActions>
            </Dialog>
        </FullWidthContainer>
    );
};

export default SupervisorResearchWorkView;